import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (username, password) => api.post('/auth/login', { username, password });
export const getMe = () => api.get('/auth/me');
export const initAdmin = () => api.post('/init/admin');

// Users
export const getUsuarios = () => api.get('/usuarios');
export const createUsuario = (data) => api.post('/usuarios', data);
export const updateUsuario = (id, data) => api.put(`/usuarios/${id}`, data);
export const resetPassword = (id, newPassword) => api.post(`/usuarios/${id}/reset-password?new_password=${encodeURIComponent(newPassword)}`);
export const deleteUsuario = (id, motivo) => api.delete(`/usuarios/${id}?motivo=${encodeURIComponent(motivo)}`);

// Ciclos Escolares
export const getCiclos = () => api.get('/ciclos-escolares');
export const createCiclo = (data) => api.post('/ciclos-escolares', data);
export const activarCiclo = (id) => api.put(`/ciclos-escolares/${id}/activar`);

// Calendario
export const getCalendario = (cicloId) => api.get('/calendario', { params: { ciclo_escolar_id: cicloId } });
export const createCalendarioEntry = (data) => api.post('/calendario', data);
export const deleteCalendarioEntry = (fecha, cicloId) => api.delete(`/calendario/${fecha}`, { params: { ciclo_escolar_id: cicloId } });

// Alumnos
export const getAlumnos = (params) => api.get('/alumnos', { params });
export const getAlumno = (id) => api.get(`/alumnos/${id}`);
export const createAlumno = (data) => api.post('/alumnos', data);
export const updateAlumno = (id, data) => api.put(`/alumnos/${id}`, data);
export const darBajaAlumno = (id, motivo, tipoBaja = 'baja_temporal') =>
  api.post(`/alumnos/${id}/baja`, { motivo, tipo_baja: tipoBaja });
export const reactivarAlumno = (id) => api.post(`/alumnos/${id}/reactivar`);
export const deleteAlumno = (id, motivo) => api.delete(`/alumnos/${id}?motivo=${encodeURIComponent(motivo)}`);
export const bulkUploadAlumnos = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/alumnos/bulk-upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Asistencia
export const getAsistencia = (params) => api.get('/asistencia', { params });
export const createAsistencia = (data) => api.post('/asistencia', data);
export const bulkCreateAsistencia = (data) => api.post('/asistencia/bulk', data);
export const updateAsistencia = (id, estado, justificacion) => 
  api.put(`/asistencia/${id}?estado=${estado}${justificacion ? `&justificacion=${encodeURIComponent(justificacion)}` : ''}`);

// Expediente
export const getExpedientes = (params) => api.get('/expediente', { params });
export const createExpediente = (data) => api.post('/expediente', data);
export const updateExpediente = (id, descripcion, sancion) => {
  let url = `/expediente/${id}?`;
  if (descripcion) url += `descripcion=${encodeURIComponent(descripcion)}&`;
  if (sancion) url += `sancion=${encodeURIComponent(sancion)}`;
  return api.put(url);
};
export const deleteExpediente = (id, motivo) => api.delete(`/expediente/${id}?motivo=${encodeURIComponent(motivo)}`);

// Bitacora
export const getBitacora = (params) => api.get('/bitacora', { params });
export const createBitacora = (data) => api.post('/bitacora', data);
export const marcarBitacoraLeido = (id) => api.put(`/bitacora/${id}/marcar-leido`);
export const atenderBitacora = (id, comentario) => 
  api.put(`/bitacora/${id}/atender${comentario ? `?comentario=${encodeURIComponent(comentario)}` : ''}`);
export const createIncidenciaEscolarBulk = (data) => api.post('/expediente/incidencia-escolar-bulk', data);

// PATRE
export const getPatre = (params) => api.get('/patre', { params });
export const getPatreAlumnos = () => api.get('/patre/alumnos');
export const createPatreRegistro = (data) => api.post('/patre', data);
export const updatePatre = (id, estado, observaciones, estrategia) => {
  let url = `/patre/${id}?`;
  if (estado) url += `estado=${estado}&`;
  if (observaciones) url += `observaciones=${encodeURIComponent(observaciones)}&`;
  if (estrategia) url += `estrategia_aplicada=${encodeURIComponent(estrategia)}`;
  return api.put(url);
};
export const bulkUploadPatre = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/patre/bulk-upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Alertas
export const getAlertas = (params) => api.get('/alertas', { params });
export const atenderAlerta = (id, notas) => 
  api.put(`/alertas/${id}/atender${notas ? `?notas=${encodeURIComponent(notas)}` : ''}`);

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');
export const getAttendanceChart = (days) => api.get('/dashboard/attendance-chart', { params: { days } });
export const getRecentIncidents = (limit) => api.get('/dashboard/recent-incidents', { params: { limit } });

// Audit
export const getAuditLogs = (params) => api.get('/audit', { params });

// Reports
export const exportAsistenciaExcel = (params) => 
  api.get('/reportes/asistencia/excel', { params, responseType: 'blob' });
export const exportAsistenciaPdf = (params) => 
  api.get('/reportes/asistencia/pdf', { params, responseType: 'blob' });
export const exportExpedienteExcel = (params) => 
  api.get('/reportes/expediente/excel', { params, responseType: 'blob' });
export const exportExpedientePdf = (params) => 
  api.get('/reportes/expediente/pdf', { params, responseType: 'blob' });
export const exportInscripcionesPdf = (params) => 
  api.get('/reportes/inscripciones/pdf', { params, responseType: 'blob' });

// Dashboard Indicadores
export const getDashboardIndicadores = () => api.get('/dashboard/indicadores');
export const getCalendarioResumen = () => api.get('/dashboard/resumen-calendario');

// Configuracion
export const uploadLogo = (file, cicloId) => {
  const formData = new FormData();
  formData.append('file', file);
  const params = cicloId ? `?ciclo_escolar_id=${cicloId}` : '';
  return api.post(`/configuracion/logo${params}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const getLogoInfo = (cicloId) => api.get('/configuracion/logo/info', { params: { ciclo_escolar_id: cicloId } });
export const getLogoUrl = (cicloId) => {
  const token = localStorage.getItem('token');
  const params = cicloId ? `ciclo_escolar_id=${cicloId}&` : '';
  return `${BACKEND_URL}/api/configuracion/logo?${params}auth=${token}`;
};

// Templates
export const downloadAlumnosTemplate = () => 
  api.get('/templates/alumnos', { responseType: 'blob' });
export const downloadPatreTemplate = () => 
  api.get('/templates/patre', { responseType: 'blob' });
export const downloadUsuariosTemplate = () => 
  api.get('/templates/usuarios', { responseType: 'blob' });
export const downloadInscripcionesTemplate = () => 
  api.get('/templates/inscripciones', { responseType: 'blob' });

// Inscripciones
export const getInscripciones = (params) => api.get('/inscripciones', { params });
export const getInscripcion = (id) => api.get(`/inscripciones/${id}`);
export const createInscripcion = (data) => api.post('/inscripciones', data);
export const updateInscripcion = (id, data) => api.put(`/inscripciones/${id}`, data);
export const deleteInscripcion = (id, motivo) => api.delete(`/inscripciones/${id}?motivo=${encodeURIComponent(motivo)}`);
export const crearReinscripcion = (data) => api.post('/inscripciones/reinscripcion', data);

// Backup / Restore
export const downloadBackup = () => api.get('/configuracion/backup', { responseType: 'blob' });
export const restoreBackup = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/configuracion/backup/restore?confirmar=true', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const uploadFotoInscripcion = (id, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/inscripciones/${id}/foto`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const getFotoInscripcion = (id) => {
  const token = localStorage.getItem('token');
  return api.get(`/inscripciones/${id}/foto?auth=${token}`, { responseType: 'blob' });
};
export const aprobarInscripcion = (id) => api.post(`/inscripciones/${id}/aprobar`);
export const integrarInscripcion = (id) => api.post(`/inscripciones/${id}/integrar`);
export const generarCredencial = (id) => api.post(`/inscripciones/${id}/credencial`, {}, { responseType: 'blob' });
export const getEstadisticasInscripciones = (cicloId) => api.get('/inscripciones/estadisticas/resumen', { params: { ciclo_escolar_id: cicloId } });
export const bulkUploadInscripciones = (file, cicloId) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/inscripciones/bulk-upload?ciclo_escolar_id=${cicloId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const exportInscripcionesExcel = (params) => 
  api.get('/reportes/inscripciones/excel', { params, responseType: 'blob' });
export const promoverCicloEscolar = (data) => api.post('/ciclos-escolares/promover', data);

// Inscripciones extras (nuevo)
export const precargarInscripcionByCurp = (curp) =>
  api.get('/inscripciones-precarga', { params: { curp } });
export const comprobanteInscripcion = (id) =>
  api.get(`/inscripciones/${id}/comprobante`, { responseType: 'blob' });

// Reportes con gráfico (PDF mensual/semanal adaptativo)
export const exportAsistenciaPdfGrafico = (params) =>
  api.get('/reportes/asistencia/pdf-grafico', { params, responseType: 'blob' });

// Reporte expediente con gráfico (pie + barras + listado)
export const exportExpedientePdfGrafico = (params) =>
  api.get('/reportes/expediente/pdf-grafico', { params, responseType: 'blob' });

// Credencialización (módulo independiente)
export const uploadCredencialTemplate = (file, lado) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/credenciales/template?lado=${lado}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const getCredencialTemplateInfo = () => api.get('/credenciales/template/info');
export const generarCredencialesBulk = (data) =>
  api.post('/credenciales/generar-bulk', data, { responseType: 'blob' });

export default api;
